import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import {
  applyRobotsMeta,
  mergeIndexabilityReports,
  resolveIndexability,
  sameIndexabilityReport,
} from '../lib/indexability'
import type { IndexabilityReport } from '../lib/indexability'

type PublishReport = (id: symbol, report: IndexabilityReport | null | undefined) => void

type IndexabilityContextValue = Readonly<{
  report: IndexabilityReport | null
  publish: PublishReport
}>

const IndexabilityContext = createContext<IndexabilityContextValue>({
  report: null,
  publish: () => {},
})

const IndexabilityProvider = ({ children }: { children: ReactNode }) => {
  const entries = useRef(new Map<symbol, IndexabilityReport | null>())
  const [report, setReport] = useState<IndexabilityReport | null>(null)

  const publish = useCallback<PublishReport>((id, next) => {
    if (next === undefined) entries.current.delete(id)
    else entries.current.set(id, next)

    const currentReports: Array<IndexabilityReport | null> = []
    entries.current.forEach(value => { currentReports.push(value) })
    const merged = mergeIndexabilityReports(currentReports)
    setReport(current => sameIndexabilityReport(current, merged) ? current : merged)
  }, [])

  const value = useMemo(() => ({ report, publish }), [publish, report])

  return (
    <IndexabilityContext.Provider value={ value }>
      { children }
    </IndexabilityContext.Provider>
  )
}

const useIndexabilityReport = (report: IndexabilityReport | null) => {
  const { publish } = useContext(IndexabilityContext)
  const id = useRef(Symbol('indexability'))
  const entity = report?.entity
  const browseSettled = report?.browseSettled
  const browseResultCount = report?.browseResultCount

  useEffect(() => {
    const next: IndexabilityReport | null = entity || browseSettled !== undefined || browseResultCount !== undefined
      ? { entity, browseSettled, browseResultCount }
      : null
    publish(id.current, next)
    return () => publish(id.current, undefined)
  }, [browseResultCount, browseSettled, entity, publish])
}

const IndexabilityMeta = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { report } = useContext(IndexabilityContext)

  const resolved = useMemo(() => resolveIndexability({
    pathname: location.pathname,
    search: location.search,
    report,
  }), [location.pathname, location.search, report])

  const titleBeforeNotFound = useRef<string | null>(null)

  useEffect(() => {
    applyRobotsMeta(resolved)
    const notFoundTitle = `${t('errors.generic_not_found.title')} • HOLIC`
    const notFound = resolved.reason === 'unknown_route' || resolved.reason === 'missing_entity'
    if (notFound) {
      if (document.title !== notFoundTitle) titleBeforeNotFound.current = document.title
      document.title = notFoundTitle
      return
    }
    if (titleBeforeNotFound.current !== null && document.title === notFoundTitle) {
      document.title = titleBeforeNotFound.current
    }
    titleBeforeNotFound.current = null
  }, [resolved, t])

  return null
}

export { IndexabilityMeta, IndexabilityProvider, useIndexabilityReport }
